"use client";

import { useEffect, useMemo, useState } from "react";
import { Button, Grid, Icon, Text, View } from "@/components";
import { useLayout } from "@/providers";
import cn from "@/utils/cn";

const DAYS = [
  "sundayShort",
  "mondayShort",
  "tuesdayShort",
  "wednesdayShort",
  "thursdayShort",
  "fridayShort",
  "saturdayShort"
];
const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];

/**
 * Calendar Component
 *
 * @description
 * An interactive calendar component that allows users to navigate through months
 * and select dates. It highlights the current date and the selected date.
 *
 * It uses the `Grid` component to render the days in a 7-column layout.
 * Translations are used for day and month names.
 *
 * @component
 * @returns {JSX.Element} The rendered interface for the calendar.
 *
 * @example
 * ```tsx
 * <Calendar />
 * ```
 */
const Calendar = () => {
  const { t } = useLayout();

  const [viewDate, setViewDate] = useState(new Date(2026, 1, 1));

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    new Date(2026, 1, 1)
  );

  const [today, setToday] = useState<Date | null>(null);

  // Set today's date only on client-side to avoid hydration mismatch
  useEffect(() => {
    setToday(new Date());
  }, []);

  const isToday = (day: number) =>
    today !== null &&
    day === today.getDate() &&
    viewDate.getMonth() === today.getMonth() &&
    viewDate.getFullYear() === today.getFullYear();

  const daysInMonth = useMemo(
    () =>
      new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate(),
    [viewDate]
  );

  const firstDay = useMemo(
    () => new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay(),
    [viewDate]
  );

  const days = useMemo(() => {
    const d = [];

    for (let i = 0; i < firstDay; i++) {
      d.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      d.push(i);
    }
    return d;
  }, [daysInMonth, firstDay]);

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    setSelectedDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), day));
  };

  const isSelected = (day: number) =>
    selectedDate?.getDate() === day &&
    selectedDate?.getMonth() === viewDate.getMonth() &&
    selectedDate?.getFullYear() === viewDate.getFullYear();

  return (
    <View className="flex h-full min-h-[400px] w-full flex-col rounded-[2rem] border border-border bg-card p-8 shadow-sm">
      <View className="mb-8 flex items-center justify-between">
        <Button
          onClick={handlePrevMonth}
          className="text-muted-foreground hover:text-foreground"
        >
          <Icon
            name="arrow-left"
            className="text-xl"
            aria-label="Previous month"
          />
        </Button>
        <Text type="h3" className="text-xl font-bold text-foreground">
          {t(MONTHS[viewDate.getMonth()])} {viewDate.getFullYear()}
        </Text>
        <Button
          onClick={handleNextMonth}
          className="text-muted-foreground hover:text-foreground"
        >
          <Icon
            name="arrow-right"
            className="text-xl"
            aria-label="Next month"
          />
        </Button>
      </View>

      <Grid className="grid-cols-7 gap-y-6 text-center">
        {DAYS.map((day) => (
          <Text key={day} className="text-sm font-medium text-muted-foreground">
            {t(day)}
          </Text>
        ))}
        {days.map((day, index) => (
          <View
            key={day ? day : `emptyDay-${index}`}
            className="flex items-center justify-center"
          >
            {day ? (
              <Button
                onClick={() => handleDateClick(day)}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full text-base font-medium transition-all hover:bg-accent text-muted-foreground dark:text-muted-foreground",
                  isToday(day) && "ring-2 ring-primary text-foreground",
                  isSelected(day) &&
                    "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
              >
                {day}
              </Button>
            ) : (
              <View />
            )}
          </View>
        ))}
      </Grid>
    </View>
  );
};

export default Calendar;
